// Teinte des icônes réseaux (navigateur uniquement : canvas 2D).
// Un PNG ne se recolore pas en CSS dans un mail : on génère la variante teintée à l'enregistrement
// de la charte, à partir des PNG noirs officiels servis sous /icons/, et on l'envoie dans le bucket
// sous le chemin déterministe calculé par le cœur (core/social.ts). Le fichier source n'est jamais
// modifié ni redessiné. Recoloration pixel par pixel : le noir prend la teinte, le blanc reste
// blanc (le « f » de Facebook et le « in » de LinkedIn sont des lettres blanches OPAQUES, pour tenir
// en mode sombre), les gris d'anticrénelage sont interpolés, l'alpha est conservé tel quel.
import type { Api } from './api.ts';
import {
  type AssetPlan,
  type Brand,
  effectiveStyles,
  ICON_SOURCE_SIZE,
  isBlack,
  socialLinks,
  tintedIconPath,
} from '../../supabase/functions/_shared/core/index.ts';

async function loadImage(url: string): Promise<HTMLImageElement> {
  const img = new Image();
  img.decoding = 'async';
  await new Promise<void>((resolve, reject) => {
    img.onload = () => resolve();
    img.onerror = () => reject(new Error(`Icône introuvable : ${url}`));
    img.src = url;
  });
  return img;
}

function hexToRgb(color: string): [number, number, number] {
  const v = color.trim().replace('#', '');
  const h = v.length === 3 ? [...v].map((c) => c + c).join('') : v;
  const n = /^[0-9a-f]{6}$/i.test(h) ? parseInt(h, 16) : 0;
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
}

/** PNG teinté (même taille que la source), alpha conservé. */
export async function tintPng(sourceUrl: string, color: string): Promise<Uint8Array> {
  const img = await loadImage(sourceUrl);
  const size = ICON_SOURCE_SIZE;
  const canvas = document.createElement('canvas');
  canvas.width = size;
  canvas.height = size;
  const cx = canvas.getContext('2d');
  if (!cx) throw new Error('Canvas indisponible dans ce navigateur.');
  cx.drawImage(img, 0, 0, size, size);
  const rgb = hexToRgb(color);
  const data = cx.getImageData(0, 0, size, size);
  const d = data.data;
  for (let i = 0; i < d.length; i += 4) {
    if (d[i + 3] === 0) continue;
    const white = (0.2126 * d[i] + 0.7152 * d[i + 1] + 0.0722 * d[i + 2]) / 255; // 0 = noir → teinte, 1 = blanc → blanc
    d[i] = Math.round(rgb[0] + (255 - rgb[0]) * white);
    d[i + 1] = Math.round(rgb[1] + (255 - rgb[1]) * white);
    d[i + 2] = Math.round(rgb[2] + (255 - rgb[2]) * white);
  }
  cx.putImageData(data, 0, 0);
  const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, 'image/png'));
  if (!blob) throw new Error('Génération de l’icône impossible.');
  return new Uint8Array(await blob.arrayBuffer());
}

/**
 * Génère et envoie les icônes teintées d'une charte (un réseau = un fichier). Rien à faire si la
 * teinte est noire : le rendu pointe alors sur l'asset commun. Renvoie le nombre d'icônes envoyées.
 */
export async function uploadTintedIcons(api: Api, brand: Brand, iconsBase: string): Promise<number> {
  const color = effectiveStyles(brand).social.color; // rôle « icônes réseaux » de la charte
  if (isBlack(color)) return 0;
  let count = 0;
  for (const { network } of socialLinks(brand)) {
    const bytes = await tintPng(`${iconsBase.replace(/\/$/, '')}/icons/${network.id}.png`, color);
    const plan: AssetPlan = {
      kind: 'raster',
      path: tintedIconPath(brand.id, network.id, color),
      bytes,
      contentType: 'image/png',
      upsert: true, // chemin déterministe : même teinte = même fichier
    };
    await api.uploadAsset(plan);
    count++;
  }
  return count;
}
